import { decryptPDF, isEncrypted } from '@pdfsmaller/pdf-decrypt';
import { encryptPdfWithFallbacks, tryNormalizePdfBytes } from '../shared/pdfEncryptUtils';

async function readBytes(file) {
    return new Uint8Array(await file.arrayBuffer());
}

/**
 * A throwaway owner password. If the owner password equals the one the reader types, that
 * reader is authenticated as the OWNER and viewers hand owners every right — the print / copy
 * choices would be ignored. With a separate secret the typed password only opens the file as a
 * USER, so the restrictions below really apply. The owner password is never shown or stored.
 */
function randomOwnerPassword() {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function buildLockOptions(_password, { allowPrint, allowCopy }) {
    return {
        ownerPassword: randomOwnerPassword(),
        allowPrinting: allowPrint,
        allowHighQualityPrint: allowPrint,
        allowCopying: allowCopy,
        allowExtraction: allowCopy,
        allowModifying: false,
        allowAnnotating: false,
        allowFillingForms: false,
        allowAssembly: false,
    };
}

/** @returns {Promise<{ encrypted: boolean, algorithm?: string }>} */
export async function getPdfEncryptionInfo(file) {
    const bytes = await readBytes(file);
    return isEncrypted(bytes);
}

/**
 * Add password protection to a PDF.
 * @param {File} file
 * @param {string} password
 * @param {{ allowPrint: boolean, allowCopy: boolean }} permissions
 */
export async function lockPdf(file, password, { allowPrint, allowCopy }) {
    const bytes = await readBytes(file);
    const info = await isEncrypted(bytes);
    if (info.encrypted) throw new Error('alreadyEncrypted');
    if (!password) throw new Error('emptyPassword');

    const encrypted = await encryptPdfWithFallbacks(
        bytes,
        password,
        buildLockOptions(password, { allowPrint, allowCopy }),
    );
    return new Blob([encrypted], { type: 'application/pdf' });
}

/**
 * Remove password protection from a PDF.
 * @param {File} file
 * @param {string} password
 */
export async function unlockPdf(file, password) {
    const bytes = await readBytes(file);
    const info = await isEncrypted(bytes);
    if (!info.encrypted) throw new Error('notEncrypted');
    if (!password) throw new Error('emptyPassword');

    try {
        const decrypted = await decryptPDF(bytes, password);
        let output = new Uint8Array(decrypted);
        try {
            output = await tryNormalizePdfBytes(output);
        } catch {
            // Keep decrypted bytes if normalization fails.
        }
        return new Blob([output], { type: 'application/pdf' });
    } catch (err) {
        const msg = String(err?.message || '');
        if (msg.toLowerCase().includes('incorrect password')) throw new Error('wrongPassword');
        throw err;
    }
}
